import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, Image, StyleSheet, TouchableOpacity, ScrollView,
  ActivityIndicator, useWindowDimensions,
} from 'react-native';
import {
  ref, listAll, getDownloadURL, getMetadata, getBlob, uploadBytes, deleteObject,
  StorageReference,
} from 'firebase/storage';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db, storage } from '../firebase/firebase';
import { RefreshCw, Download, ImageOff, Database } from 'lucide-react';

const C = {
  primary: '#21808D', primaryLight: '#e8f5f7',
  danger: '#ef4444', dangerLight: '#fee2e2',
  success: '#16a34a', successLight: '#dcfce7',
  bg: '#0E1512', card: '#fff',
  textPrimary: '#1a2332', textMuted: '#64748b', border: '#e2e8f0',
};

const RESTAURANT_COLLECTION = 'zapee_restaurant_partner';
const PRODUCT_COLLECTION = 'zapee_restaurant_products';

// Sub-directories per restaurant + where each pulls its images from.
const PHOTO_TYPES = ['logo', 'cover', 'banner', 'category', 'product', 'features'];
const COVER_FIELDS = ['media.cover', 'cover', 'media.coverImage', 'coverImage', 'coverPhoto', 'media.coverPhoto'];

interface PhotoFile { fullPath: string; name: string; url: string; }
interface Restaurant { id: string; store: string; name: string; root: string; data: any; }

const dirOf = (p: string) => { const i = p.lastIndexOf('/'); return i === -1 ? '(root)' : p.slice(0, i); };

function pathFromUrl(url: string): string | null {
  if (typeof url !== 'string' || !url.includes('/o/')) return null;
  const m = url.match(/\/o\/([^?]+)/);
  if (!m) return null;
  try { return decodeURIComponent(m[1]); } catch { return m[1]; }
}

const isPlaceholder = (r: StorageReference) =>
  r.name === '' || r.fullPath.endsWith('/') || r.name.startsWith('.');

const isPlainObject = (v: any) =>
  v != null && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype;

const getByPath = (obj: any, path: string) =>
  path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);

function sanitizeFolder(raw: string): string {
  return raw.replace(/[#\[\]*?]/g, '').replace(/\\/g, '/')
    .split('/').map(s => s.trim()).filter(Boolean).join('/');
}

function collectStorageUrls(value: any, out: Set<string>) {
  if (typeof value === 'string') { if (pathFromUrl(value)) out.add(value); }
  else if (Array.isArray(value)) value.forEach(v => collectStorageUrls(v, out));
  else if (isPlainObject(value)) Object.values(value).forEach(v => collectStorageUrls(v, out));
}

function deepReplace(value: any, repl: Map<string, string>): any {
  if (typeof value === 'string') return repl.get(value) ?? value;
  if (Array.isArray(value)) return value.map(v => deepReplace(v, repl));
  if (isPlainObject(value)) {
    const o: any = {};
    for (const [k, v] of Object.entries(value)) o[k] = deepReplace(v, repl);
    return o;
  }
  return value;
}

async function listAllFiles(folderRef: StorageReference): Promise<StorageReference[]> {
  const res = await listAll(folderRef);
  let files = [...res.items];
  for (const prefix of res.prefixes) files = files.concat(await listAllFiles(prefix));
  return files;
}

export default function RestaurantMedia() {
  const { width } = useWindowDimensions();
  const cols = width < 700 ? 2 : width < 1100 ? 3 : 4;

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const [photosByDir, setPhotosByDir] = useState<Record<string, PhotoFile[]>>({});
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [productsByStore, setProductsByStore] = useState<Record<string, { id: string; data: any }[]>>({});

  const [selRest, setSelRest] = useState<string>('');
  const [selType, setSelType] = useState<string>('logo');
  // Staged work, keyed by `${restId}|${type}`: filled by "Bring images"
  // (files copied in), consumed by "Update database" (refs updated, old deleted).
  // Persisted to localStorage so a page refresh doesn't lose un-finalized work.
  type Pending = Record<string, {
    docUpdates: { col: string; id: string; upd: any }[];
    oldUrls: string[];
    count: number;
  }>;
  const [pending, setPending] = useState<Pending>(() => {
    try { return JSON.parse(localStorage.getItem('rm_pending') || '{}'); } catch { return {}; }
  });
  useEffect(() => {
    try { localStorage.setItem('rm_pending', JSON.stringify(pending)); } catch { /* ignore */ }
  }, [pending]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setProgress('Listing storage…');
      const refs = (await listAllFiles(ref(storage))).filter(r => !isPlaceholder(r));
      const byDir: Record<string, PhotoFile[]> = {};
      await Promise.all(refs.map(async (r) => {
        let url = '';
        try { url = await getDownloadURL(r); } catch { /* ignore */ }
        (byDir[dirOf(r.fullPath)] ??= []).push({ fullPath: r.fullPath, name: r.name, url });
      }));

      setProgress('Loading restaurants & products…');
      const [restSnap, prodSnap] = await Promise.all([
        getDocs(collection(db, RESTAURANT_COLLECTION)),
        getDocs(collection(db, PRODUCT_COLLECTION)),
      ]);

      const rests: Restaurant[] = restSnap.docs.map(d => {
        const data: any = d.data();
        return {
          id: d.id,
          store: String(data.storeuid || data.uid || data.id || d.id),
          name: data.storename || data.name || d.id,
          root: sanitizeFolder(data.storename || data.name || ''),
          data,
        };
      }).filter(r => r.root).sort((a, b) => a.name.localeCompare(b.name));

      const prodBy: Record<string, { id: string; data: any }[]> = {};
      prodSnap.docs.forEach(p => {
        const data: any = p.data();
        const store = String(data.branch || '');
        (prodBy[store] ??= []).push({ id: p.id, data });
      });

      setPhotosByDir(byDir);
      setRestaurants(rests);
      setProductsByStore(prodBy);
      setSelRest(prev => prev && rests.some(r => r.id === prev) ? prev : (rests[0]?.id ?? ''));
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
      setProgress('');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rest = restaurants.find(r => r.id === selRest);
  const folderOf = (type: string) => rest ? `${rest.root}/${type}` : '';
  const imagesFor = (type: string) => (rest ? photosByDir[folderOf(type)] ?? [] : []);

  // STEP 1 — Bring images: copy this restaurant's images of one type into
  // <root>/<type>. Originals are NOT deleted and the DB is NOT touched; the
  // resulting reference changes are staged in `pending` for step 2.
  const bringImages = async (type: string) => {
    if (!rest || !rest.root) { window.alert('Pick a restaurant first.'); return; }
    const key = `${rest.id}|${type}`;
    if (pending[key]?.count) {
      window.alert('Already brought — click "Update database" to finalize before bringing again.');
      return;
    }
    const destFolder = `${rest.root}/${type}`;
    setWorking(true);
    setProgress('Bringing images…');
    let moved = 0, failed = 0;
    const usedNames = new Set<string>();
    const docUpdates: { col: string; id: string; upd: any }[] = [];
    const oldUrls: string[] = [];

    // Copy one file into the destination folder; returns its new URL (no delete).
    // If a copy with the same name is already in the folder (e.g. from an earlier
    // Bring whose staged list was lost), reuse it instead of duplicating.
    const copyUrl = async (url: string): Promise<string | null> => {
      const op = pathFromUrl(url);
      if (!op || dirOf(op) === destFolder) return null;
      const base0 = op.split('/').pop() || `file_${moved}`;
      const existing = (photosByDir[destFolder] || []).find(f => f.name === base0);
      if (existing?.url) { usedNames.add(`${destFolder}/${base0}`); moved++; setProgress(`Brought ${moved}…`); return existing.url; }
      try {
        const srcRef = ref(storage, op);
        let base = base0;
        let dst = `${destFolder}/${base}`;
        if (usedNames.has(dst)) {
          const dot = base.lastIndexOf('.');
          base = (dot > 0 ? base.slice(0, dot) : base) + `_${moved}` + (dot > 0 ? base.slice(dot) : '');
          dst = `${destFolder}/${base}`;
        }
        let ct: string | undefined;
        try { ct = (await getMetadata(srcRef)).contentType || undefined; } catch { /* ignore */ }
        const blob = await getBlob(srcRef);
        await uploadBytes(ref(storage, dst), blob, ct ? { contentType: ct } : undefined);
        const nu = await getDownloadURL(ref(storage, dst));
        usedNames.add(dst);
        moved++;
        setProgress(`Brought ${moved}…`);
        return nu;
      } catch { failed++; return null; }
    };

    try {
      if (type === 'logo') {
        const url = rest.data?.media?.logo || rest.data?.image || '';
        if (url) {
          const nu = await copyUrl(url);
          if (nu) { docUpdates.push({ col: RESTAURANT_COLLECTION, id: rest.id, upd: { image: nu, 'media.logo': nu } }); oldUrls.push(url); }
        }
      } else if (type === 'cover') {
        let cf = '', cu = '';
        for (const f of COVER_FIELDS) {
          const v = getByPath(rest.data, f);
          if (typeof v === 'string' && pathFromUrl(v)) { cu = v; cf = f; break; }
        }
        if (!cu) { setWorking(false); setProgress(''); window.alert('No cover image field found on this restaurant.'); return; }
        const nu = await copyUrl(cu);
        if (nu) { docUpdates.push({ col: RESTAURANT_COLLECTION, id: rest.id, upd: { [cf]: nu } }); oldUrls.push(cu); }
      } else if (type === 'banner') {
        const arr = (rest.data?.media?.bannerImages || rest.data?.bannerImages || []) as string[];
        const repl = new Map<string, string>();
        for (const u of arr) { const nu = await copyUrl(u); if (nu) repl.set(u, nu); }
        if (repl.size) {
          const na = arr.map(u => repl.get(u) ?? u);
          docUpdates.push({ col: RESTAURANT_COLLECTION, id: rest.id, upd: { bannerImages: na, 'media.bannerImages': na } });
          oldUrls.push(...repl.keys());
        }
      } else if (type === 'category') {
        const cats = rest.data?.categories;
        const urls = new Set<string>(); collectStorageUrls(cats, urls);
        const repl = new Map<string, string>();
        for (const u of urls) { const nu = await copyUrl(u); if (nu) repl.set(u, nu); }
        if (repl.size) {
          docUpdates.push({ col: RESTAURANT_COLLECTION, id: rest.id, upd: { categories: deepReplace(cats, repl) } });
          oldUrls.push(...repl.keys());
        }
      } else if (type === 'product') {
        const prods = productsByStore[rest.store] ?? [];
        for (const p of prods) {
          const u = p.data?.image || '';
          if (!u) continue;
          const nu = await copyUrl(u);
          if (nu) { docUpdates.push({ col: PRODUCT_COLLECTION, id: p.id, upd: { image: nu } }); oldUrls.push(u); }
        }
      } else if (type === 'features') {
        const prods = productsByStore[rest.store] ?? [];
        for (const p of prods) {
          const arr = (p.data?.featureImages || []) as string[];
          if (!arr.length) continue;
          const repl = new Map<string, string>();
          for (const u of arr) { const nu = await copyUrl(u); if (nu) repl.set(u, nu); }
          if (repl.size) {
            const na = arr.map(u => repl.get(u) ?? u);
            docUpdates.push({ col: PRODUCT_COLLECTION, id: p.id, upd: { featureImages: na } });
            oldUrls.push(...repl.keys());
          }
        }
      }

      if (moved > 0) setPending(prev => ({ ...prev, [key]: { docUpdates, oldUrls, count: moved } }));
      setWorking(false); setProgress('');
      await load();
      window.alert(
        moved === 0 && failed === 0
          ? 'Nothing to bring — all images of this type are already in the folder (or none exist).'
          : `Brought ${moved} image(s) into the folder.${failed ? `\nFailed: ${failed}` : ''}\n\n` +
            `The database still points to the old copies — click "Update database" to finalize.` +
            (failed && !moved ? `\n\nIf nothing moved, the bucket likely needs CORS enabled for downloads.` : '')
      );
    } catch (e: any) {
      setWorking(false); setProgress('');
      window.alert(`Failed: ${e?.message || e}`);
    }
  };

  // STEP 2 — Update database: point the Firestore references at the brought-in
  // copies, then delete the old originals.
  const updateDatabase = async (type: string) => {
    if (!rest) return;
    const key = `${rest.id}|${type}`;
    const p = pending[key];
    if (!p || p.count === 0) { window.alert('Bring images first, then update the database.'); return; }
    if (!window.confirm(`Update ${p.docUpdates.length} database record(s) to the new ${type} folder and remove the old files?`)) return;

    setWorking(true);
    setProgress('Updating database…');
    let updated = 0, failed = 0;
    for (const du of p.docUpdates) {
      try { await updateDoc(doc(db, du.col, du.id), du.upd); updated++; }
      catch { failed++; }
    }
    // Only delete originals if every record updated cleanly.
    if (failed === 0) {
      await Promise.all(p.oldUrls.map(u => {
        const op = pathFromUrl(u);
        return op ? deleteObject(ref(storage, op)).catch(() => {}) : Promise.resolve();
      }));
    }
    setPending(prev => { const n = { ...prev }; delete n[key]; return n; });
    setWorking(false); setProgress('');
    await load();
    window.alert(`Database updated: ${updated}${failed ? `\nFailed: ${failed} (old files kept)` : ''}`);
  };

  // Bulk finalize for EVERY restaurant, derived purely from the files already
  // sitting in each <root>/<type> folder (no in-memory staged list needed).
  // For each DB image whose URL still points outside its folder, it finds the
  // already-copied file by name, repoints the reference, and deletes the old.
  const updateAllFromStorage = async (type: string) => {
    if (!window.confirm(
      `Update the database for ALL restaurants' "${type}" images using the copies already in their folders, and delete the old originals?\n\n` +
      `This only repoints references to files that are already in the folder.`
    )) return;

    setWorking(true);
    setProgress('Updating database for all restaurants…');
    let updated = 0, failed = 0, skipped = 0;

    // Match an old image URL to a file already present in destFolder (by name).
    const matchIn = (folderMap: Map<string, string>, oldUrl: string): string | null => {
      const op = pathFromUrl(oldUrl);
      if (!op || dirOf(op) === '') return null;
      return folderMap.get(op.split('/').pop() || '') || null;
    };
    const delOld = (oldUrl: string) => {
      const op = pathFromUrl(oldUrl);
      return op ? deleteObject(ref(storage, op)).catch(() => {}) : Promise.resolve();
    };

    try {
      for (const r of restaurants) {
        const destFolder = `${r.root}/${type}`;
        const files = photosByDir[destFolder] || [];
        if (files.length === 0) continue;
        const folderMap = new Map(files.map(f => [f.name, f.url]));

        if (type === 'product') {
          for (const p of (productsByStore[r.store] ?? [])) {
            const oldUrl = p.data?.image || '';
            if (!oldUrl) continue;
            const op = pathFromUrl(oldUrl);
            if (!op) continue;
            if (dirOf(op) === destFolder) { skipped++; continue; }   // already in folder
            const nu = matchIn(folderMap, oldUrl);
            if (!nu) continue;                                       // copy not found
            try { await updateDoc(doc(db, PRODUCT_COLLECTION, p.id), { image: nu }); await delOld(oldUrl); updated++; }
            catch { failed++; }
          }
        } else if (type === 'features') {
          for (const p of (productsByStore[r.store] ?? [])) {
            const arr = (p.data?.featureImages || []) as string[];
            if (!arr.length) continue;
            const repl = new Map<string, string>();
            for (const u of arr) { const op = pathFromUrl(u); if (op && dirOf(op) !== destFolder) { const nu = matchIn(folderMap, u); if (nu) repl.set(u, nu); } }
            if (!repl.size) continue;
            const na = arr.map(u => repl.get(u) ?? u);
            try { await updateDoc(doc(db, PRODUCT_COLLECTION, p.id), { featureImages: na }); await Promise.all([...repl.keys()].map(delOld)); updated++; }
            catch { failed++; }
          }
        } else if (type === 'logo') {
          const oldUrl = r.data?.media?.logo || r.data?.image || '';
          const op = oldUrl && pathFromUrl(oldUrl);
          if (!op || dirOf(op) === destFolder) { if (op) skipped++; continue; }
          const nu = matchIn(folderMap, oldUrl);
          if (!nu) continue;
          try { await updateDoc(doc(db, RESTAURANT_COLLECTION, r.id), { image: nu, 'media.logo': nu }); await delOld(oldUrl); updated++; }
          catch { failed++; }
        } else if (type === 'cover') {
          let cf = '', cu = '';
          for (const f of COVER_FIELDS) { const v = getByPath(r.data, f); if (typeof v === 'string' && pathFromUrl(v)) { cu = v; cf = f; break; } }
          const op = cu && pathFromUrl(cu);
          if (!op || dirOf(op) === destFolder) { if (op) skipped++; continue; }
          const nu = matchIn(folderMap, cu);
          if (!nu) continue;
          try { await updateDoc(doc(db, RESTAURANT_COLLECTION, r.id), { [cf]: nu }); await delOld(cu); updated++; }
          catch { failed++; }
        } else if (type === 'banner') {
          const arr = (r.data?.media?.bannerImages || r.data?.bannerImages || []) as string[];
          const repl = new Map<string, string>();
          for (const u of arr) { const op = pathFromUrl(u); if (op && dirOf(op) !== destFolder) { const nu = matchIn(folderMap, u); if (nu) repl.set(u, nu); } }
          if (!repl.size) continue;
          const na = arr.map(u => repl.get(u) ?? u);
          try { await updateDoc(doc(db, RESTAURANT_COLLECTION, r.id), { bannerImages: na, 'media.bannerImages': na }); await Promise.all([...repl.keys()].map(delOld)); updated++; }
          catch { failed++; }
        } else if (type === 'category') {
          const cats = r.data?.categories;
          const urls = new Set<string>(); collectStorageUrls(cats, urls);
          const repl = new Map<string, string>();
          for (const u of urls) { const op = pathFromUrl(u); if (op && dirOf(op) !== destFolder) { const nu = matchIn(folderMap, u); if (nu) repl.set(u, nu); } }
          if (!repl.size) continue;
          try { await updateDoc(doc(db, RESTAURANT_COLLECTION, r.id), { categories: deepReplace(cats, repl) }); await Promise.all([...repl.keys()].map(delOld)); updated++; }
          catch { failed++; }
        }
      }

      // Clear any staged entries for this type — they're finalized now.
      setPending(prev => {
        const n = { ...prev };
        Object.keys(n).forEach(k => { if (k.endsWith(`|${type}`)) delete n[k]; });
        return n;
      });
      setWorking(false); setProgress('');
      await load();
      window.alert(
        `All restaurants — "${type}" finalized.\nUpdated: ${updated}\nAlready in folder: ${skipped}` +
        (failed ? `\nFailed: ${failed} (old files kept)` : '')
      );
    } catch (e: any) {
      setWorking(false); setProgress('');
      window.alert(`Failed: ${e?.message || e}`);
    }
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color={C.primary} />
        <Text style={styles.progressText}>{progress || 'Loading…'}</Text>
      </View>
    );
  }

  const gridImages = imagesFor(selType);
  const pendingCount = pending[`${selRest}|${selType}`]?.count ?? 0;

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <span className="material-icons-round" style={{ fontSize: 20, color: '#4ECDC4', lineHeight: 1, flexShrink: 0 }}>perm_media</span>
        <View style={{ flex: 1 }}>
          <Text style={styles.title}>Restaurant Media</Text>
          <Text style={styles.subtitle}>{restaurants.length} restaurants</Text>
        </View>
        <TouchableOpacity
          style={[styles.updateAllBtn, working && styles.btnDisabled]}
          onPress={() => updateAllFromStorage(selType)}
          disabled={working}
        >
          <Database size={15} color="#fff" />
          <Text style={styles.refreshTxt}>Update DB — all restaurants ({selType})</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.refreshBtn} onPress={load} disabled={working}>
          <RefreshCw size={16} color="#fff" />
          <Text style={styles.refreshTxt}>Refresh</Text>
        </TouchableOpacity>
      </View>

      {error && <View style={styles.errorBox}><Text style={styles.errorTxt}>{error}</Text></View>}
      {working && (
        <View style={styles.workingBox}>
          <ActivityIndicator size="small" color={C.primary} />
          <Text style={styles.workingTxt}>{progress || 'Working…'}</Text>
        </View>
      )}

      {/* ── Top bar: all restaurants ── */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.topBar} contentContainerStyle={{ paddingRight: 16 }}>
        {restaurants.length === 0 && <Text style={styles.muted}>No restaurants found.</Text>}
        {restaurants.map(r => (
          <TouchableOpacity
            key={r.id}
            style={[styles.restTab, selRest === r.id && styles.restTabActive]}
            onPress={() => setSelRest(r.id)}
          >
            <Text style={[styles.restTabTxt, selRest === r.id && styles.restTabTxtActive]} numberOfLines={1}>
              {r.name}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      {/* ── Body: left sub-dir bar + image grid ── */}
      <View style={styles.body}>
        {/* Left sub-directory bar */}
        <View style={styles.leftBar}>
          <Text style={styles.leftBarTitle} numberOfLines={1}>{rest?.root || '—'}</Text>
          {PHOTO_TYPES.map(type => {
            const count = imagesFor(type).length;
            const active = selType === type;
            return (
              <TouchableOpacity
                key={type}
                style={[styles.dirItem, active && styles.dirItemActive]}
                onPress={() => setSelType(type)}
              >
                <Text style={[styles.dirLabel, active && styles.dirLabelActive]}>{type}</Text>
                <View style={[styles.dirCount, active && styles.dirCountActive]}>
                  <Text style={[styles.dirCountTxt, active && { color: '#fff' }]}>{count}</Text>
                </View>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* Main content */}
        <ScrollView style={styles.main} contentContainerStyle={{ paddingBottom: 40 }}>
          <View style={styles.mainHeader}>
            <View style={{ flex: 1 }}>
              <Text style={styles.mainTitle}>{rest ? `${rest.root}/${selType}` : '—'}</Text>
              <Text style={styles.muted}>
                {gridImages.length} image(s) in this folder
                {pendingCount > 0 ? `  ·  ${pendingCount} brought, awaiting DB update` : ''}
              </Text>
            </View>
            <TouchableOpacity
              style={[styles.bringBtn, (working || !rest) && styles.btnDisabled]}
              onPress={() => bringImages(selType)}
              disabled={working || !rest}
            >
              <Download size={15} color="#fff" />
              <Text style={styles.bringBtnTxt}>Bring images</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.updateBtn, (working || pendingCount === 0) && styles.btnDisabled]}
              onPress={() => updateDatabase(selType)}
              disabled={working || pendingCount === 0}
            >
              <Database size={15} color="#fff" />
              <Text style={styles.bringBtnTxt}>Update database{pendingCount > 0 ? ` (${pendingCount})` : ''}</Text>
            </TouchableOpacity>
          </View>

          {gridImages.length === 0 ? (
            <View style={styles.emptyBox}>
              <ImageOff size={26} color="#94a3b8" />
              <Text style={styles.muted}>No images here yet. Use "Bring images" to pull this restaurant's {selType} photos in.</Text>
            </View>
          ) : (
            <View style={styles.grid}>
              {gridImages.map(p => (
                <View key={p.fullPath} style={[styles.cardWrap, { width: `${100 / cols}%` }]}>
                  <View style={styles.card}>
                    <View style={styles.thumbWrap}>
                      {p.url
                        ? <Image source={{ uri: p.url }} style={styles.thumb} resizeMode="cover" />
                        : <View style={[styles.thumb, styles.thumbFallback]} />}
                    </View>
                    <Text style={styles.fileName} numberOfLines={1}>{p.name}</Text>
                  </View>
                </View>
              ))}
            </View>
          )}
        </ScrollView>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg, padding: 16 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40, minHeight: 200 },
  progressText: { marginTop: 14, color: C.textMuted, fontSize: 14 },
  muted: { color: C.textMuted, fontSize: 13 },

  header: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: '#0A1712', marginTop: -16, marginHorizontal: -16, marginBottom: 12, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: 'rgba(33,128,141,0.18)' },
  title: { fontSize: 15, fontWeight: '800', color: '#fff' },
  subtitle: { fontSize: 11, color: '#8BAAB8', marginTop: 1 },
  refreshBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: C.primary, paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
  },
  refreshTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },
  updateAllBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: C.success, paddingHorizontal: 14, paddingVertical: 9, borderRadius: 8,
  },

  errorBox: { backgroundColor: C.dangerLight, borderRadius: 8, padding: 12, marginBottom: 12 },
  errorTxt: { color: C.danger, fontSize: 13 },
  workingBox: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: C.primaryLight, borderRadius: 8, padding: 10, marginBottom: 12,
  },
  workingTxt: { color: C.primary, fontSize: 13, fontWeight: '600' },

  topBar: { flexGrow: 0, marginBottom: 14 },
  restTab: {
    paddingHorizontal: 16, paddingVertical: 10, borderRadius: 24, marginRight: 8, maxWidth: 220,
    backgroundColor: C.card, borderWidth: 1, borderColor: C.border,
  },
  restTabActive: { backgroundColor: C.primary, borderColor: C.primary },
  restTabTxt: { fontSize: 13, color: C.textPrimary, fontWeight: '600' },
  restTabTxtActive: { color: '#fff' },

  body: { flex: 1, flexDirection: 'row', gap: 14 },

  leftBar: {
    width: 170, backgroundColor: C.card, borderRadius: 12, borderWidth: 1, borderColor: C.border,
    padding: 8,
  },
  leftBarTitle: { fontSize: 13, fontWeight: '800', color: C.textPrimary, padding: 8 },
  dirItem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 10, paddingVertical: 10, borderRadius: 8, marginBottom: 2,
  },
  dirItemActive: { backgroundColor: C.primary },
  dirLabel: { fontSize: 14, color: C.textPrimary, textTransform: 'capitalize' },
  dirLabelActive: { color: '#fff', fontWeight: '700' },
  dirCount: {
    minWidth: 22, paddingHorizontal: 6, paddingVertical: 1, borderRadius: 10,
    backgroundColor: C.bg, alignItems: 'center',
  },
  dirCountActive: { backgroundColor: 'rgba(255,255,255,0.25)' },
  dirCountTxt: { fontSize: 11, color: C.textMuted, fontWeight: '700' },

  main: { flex: 1, backgroundColor: C.card, borderRadius: 12, borderWidth: 1, borderColor: C.border, padding: 14 },
  mainHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 },
  mainTitle: { fontSize: 16, fontWeight: '700', color: C.textPrimary },
  bringBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: C.primary, paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8,
  },
  updateBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: C.success, paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8,
  },
  btnDisabled: { backgroundColor: '#94a3b8' },
  bringBtnTxt: { color: '#fff', fontWeight: '800', fontSize: 13 },

  emptyBox: { alignItems: 'center', gap: 10, paddingVertical: 50 },

  grid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -6 },
  cardWrap: { paddingHorizontal: 6, marginBottom: 12 },
  card: { backgroundColor: C.card, borderRadius: 10, overflow: 'hidden', borderWidth: 1, borderColor: C.border },
  thumbWrap: { width: '100%', aspectRatio: 1, backgroundColor: '#f1f5f9' },
  thumb: { width: '100%', height: '100%' },
  thumbFallback: { backgroundColor: '#e2e8f0' },
  fileName: { fontSize: 12, color: C.textPrimary, padding: 8 },
});
